import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface HospitalSite {
  hospitalCode: string;
  practiceName: string;
  isPrimary: boolean;
  status: 'granted' | 'pending' | 're_opened' | 'rejected' | 'revoked';
  requestId?: string;
  hospitalAddress?: string;
  requestedOn?: string;
  grantedOn?: string;
}

/**
 * Global state service for multi-site hospital access.
 * Manages the user's accessible hospital sites and currently selected site.
 *
 * Populated from login response (accessibleHospitals).
 * All VC data-loading pages subscribe to currentSite$ and reload on change.
 */
@Injectable({
  providedIn: 'root'
})
export class ActiveHospitalService {

  private sites$ = new BehaviorSubject<HospitalSite[]>([]);
  private selectedSite$ = new BehaviorSubject<HospitalSite | null>(null);

  /** Observable of the currently selected hospital site */
  readonly currentSite$: Observable<HospitalSite | null> = this.selectedSite$.asObservable();

  /** Observable of all accessible sites */
  readonly allSites$: Observable<HospitalSite[]> = this.sites$.asObservable();

  /** Observable: true if user has multiple granted sites */
  readonly hasMultipleSites$: Observable<boolean> = this.sites$.pipe(
    map(sites => sites.filter(s => s.status === 'granted').length > 1)
  );

  /** Observable: all granted sites only */
  readonly grantedSites$: Observable<HospitalSite[]> = this.sites$.pipe(
    map(sites => sites.filter(s => s.status === 'granted'))
  );

  constructor() {}

  /**
   * Initialize with sites from login response.
   * Auto-selects last used site or primary site.
   */
  initialize(sites: HospitalSite[]): void {
    this.sites$.next(sites);

    const grantedSites = sites.filter(s => s.status === 'granted');
    if (grantedSites.length === 0) {
      this.selectedSite$.next(null);
      return;
    }

    // Try to restore last selected site
    const lastCode = localStorage.getItem('lastSelectedHospitalCode');
    const lastSite = lastCode ? grantedSites.find(s => s.hospitalCode === lastCode) : null;

    if (lastSite) {
      this.selectedSite$.next(lastSite);
    } else {
      // Default to primary site
      const primary = grantedSites.find(s => s.isPrimary) || grantedSites[0];
      this.selectedSite$.next(primary);
      localStorage.setItem('lastSelectedHospitalCode', primary.hospitalCode);
    }
  }

  /**
   * Switch to a different hospital site.
   * Validates the site is in the granted list.
   * Returns false if site is not accessible.
   */
  switchSite(hospitalCode: string): boolean {
    const sites = this.sites$.value;
    const site = sites.find(s => s.hospitalCode === hospitalCode && s.status === 'granted');

    if (!site) {
      return false;
    }

    this.selectedSite$.next(site);
    localStorage.setItem('lastSelectedHospitalCode', hospitalCode);
    return true;
  }

  /** Get the currently selected hospital code (synchronous) */
  getSelectedHospitalCode(): string {
    return this.selectedSite$.value?.hospitalCode || '';
  }

  /** Get the currently selected hospital name (synchronous) */
  getSelectedHospitalName(): string {
    return this.selectedSite$.value?.practiceName || '';
  }

  /** Get the currently selected site (synchronous) */
  getSelectedSite(): HospitalSite | null {
    return this.selectedSite$.value;
  }

  /** Check if user has multiple granted sites (synchronous) */
  hasMultipleSites(): boolean {
    return this.sites$.value.filter(s => s.status === 'granted').length > 1;
  }

  /** Get all sites (synchronous) */
  getAllSites(): HospitalSite[] {
    return this.sites$.value;
  }

  /** Add a new site (after request approval or new request) */
  addSite(site: HospitalSite): void {
    const current = this.sites$.value;
    const exists = current.find(s => s.hospitalCode === site.hospitalCode);
    if (!exists) {
      this.sites$.next([...current, site]);
    }
  }

  /** Remove a site (after revocation or self-removal) */
  removeSite(hospitalCode: string): void {
    const current = this.sites$.value;
    this.sites$.next(current.filter(s => s.hospitalCode !== hospitalCode));

    // If removed site was selected, switch to primary
    if (this.selectedSite$.value?.hospitalCode === hospitalCode) {
      const primary = this.sites$.value.find(s => s.isPrimary && s.status === 'granted');
      if (primary) {
        this.switchSite(primary.hospitalCode);
      } else {
        this.selectedSite$.next(null);
      }
    }
  }

  /** Update a site's status (e.g., pending → granted) */
  updateSiteStatus(hospitalCode: string, status: HospitalSite['status']): void {
    const current = this.sites$.value;
    const updated = current.map(s =>
      s.hospitalCode === hospitalCode ? { ...s, status } : s
    );
    this.sites$.next(updated);
  }

  /** Clear all state (on logout) */
  clear(): void {
    this.sites$.next([]);
    this.selectedSite$.next(null);
    localStorage.removeItem('lastSelectedHospitalCode');
  }
}
